// "Test Note" — re-runs note generation for a patient against a chosen
// CONVERSATION_PROMPT version, then opens a side-by-side diff against the
// patient's final note (the one Review Note shows).
//
// The run goes through the eval pipeline, so the result is written to the eval
// summarize table flagged `is_test` and no production side-effect fires (no
// Postgres write, no doctor email, no downstream coding jobs).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/Dialog";
import { Badge } from "../ui/Badge";
import { FlaskConical, Loader2 } from "lucide-react";
import { isCapabilityEnabled } from "../../config/capabilities";
import { useToast } from "../../hooks/use-toast";
import { getTemplateDisplayTime } from "../Templates/timeUtils";
import { getSummarizeJobsByPatient } from "./patientApi";
import {
  getMostRecentSummarizeJob,
  getSummarizeJobTemplateNames,
} from "./summarizationJobUtils";
import {
  fetchTestNoteResult,
  getConversationPromptVersions,
  startTestNoteRun,
} from "./testNoteApi";
import TestNoteCompareModal from "./TestNoteCompareModal";

// The lambda re-runs the full summarization pipeline, which routinely takes a
// minute or more; poll well past that before calling it a failure. The row is
// still written if we stop polling, so a timeout loses the diff, not the run.
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Template timestamps are Unix *seconds* — the Templates page multiplies by
// 1000 before rendering, and skipping that lands every version in Jan 1970.
// Non-numeric values (an ISO string) are handed to Date untouched.
const formatTimestamp = (value) => {
  if (!value) return "";
  const seconds = Number(value);
  const date = new Date(Number.isNaN(seconds) ? value : seconds * 1000);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const TestNoteAction = ({ patientId, finalNote }) => {
  const { toast } = useToast();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [convPromptName, setConvPromptName] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [sourceJob, setSourceJob] = useState(null);

  const [running, setRunning] = useState(false);
  const [compare, setCompare] = useState(null);

  // A run polls for up to five minutes; the admin can navigate away well before
  // it resolves, so every post-await state write is gated on still being mounted.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Resolve the prompt that produced the patient's most recent note, then list
  // that template's versions so the admin can pick which one to test.
  useEffect(() => {
    if (!pickerOpen || !patientId) return undefined;

    let cancelled = false;

    (async () => {
      setLoadingVersions(true);
      setVersionsError(null);
      setVersions([]);
      setSelectedVersion(null);
      setConvPromptName(null);
      setSourceJob(null);

      const summarization = await getSummarizeJobsByPatient(patientId);
      if (cancelled) return;

      if (summarization?.error) {
        setVersionsError(summarization.error);
        setLoadingVersions(false);
        return;
      }

      const latestJob = getMostRecentSummarizeJob(summarization);
      if (!latestJob) {
        setVersionsError("No summarization jobs found for this patient.");
        setLoadingVersions(false);
        return;
      }
      setSourceJob(latestJob);

      const { convPromptName: name } = getSummarizeJobTemplateNames(latestJob);
      if (!name) {
        setVersionsError(
          "The most recent summarization job does not record which conversation prompt it used."
        );
        setLoadingVersions(false);
        return;
      }
      setConvPromptName(name);

      const result = await getConversationPromptVersions(name);
      if (cancelled) return;

      if (result.error) {
        setVersionsError(result.error);
      } else {
        setVersions(result.versions);
        // Preselect the version the note was originally generated with, so
        // running without changing anything reproduces it. Production rows only
        // record the prompt name (the pipeline resolves the default version at
        // run time), so fall back to whichever version is default today.
        const originalVersion = String(
          latestJob.templates_conv_prompt_version || ""
        ).trim();
        const preselected =
          result.versions.find((v) => v.version === originalVersion) ||
          result.versions.find((v) => v.is_default === true) ||
          result.versions[0];
        setSelectedVersion(preselected?.version || null);
      }
      setLoadingVersions(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [pickerOpen, patientId]);

  const pollForResult = useCallback(async (testSummarizeJobName) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (!isMountedRef.current) return { abandoned: true };

      const result = await fetchTestNoteResult(testSummarizeJobName);
      if (result.error) return { error: result.error };
      if (result.ready) return { job: result.job };
    }

    return {
      error:
        "Timed out waiting for the test note. The run may still finish — check the eval summarize table.",
    };
  }, []);

  const handleRun = useCallback(async () => {
    if (!patientId || running) return;

    setRunning(true);
    setPickerOpen(false);

    const started = await startTestNoteRun({
      patientId,
      convPromptName,
      convPromptVersion: selectedVersion,
    });

    if (!started.success) {
      setRunning(false);
      toast({
        variant: "destructive",
        title: "Could not start test note",
        description: started.error,
      });
      return;
    }

    toast({
      title: "Test note generating",
      description: "Running the summarization pipeline — this can take a minute.",
    });

    const { run } = started;
    const polled = await pollForResult(run.test_summarize_job_name);
    if (!isMountedRef.current || polled.abandoned) return;
    setRunning(false);

    if (polled.error) {
      toast({
        variant: "destructive",
        title: "Test note not available",
        description: polled.error,
      });
      return;
    }

    const testNote = polled.job?.soap_note || "";
    const generationError = polled.job?.summarize_error || "";
    if (!testNote.trim()) {
      toast({
        variant: "destructive",
        title: "Test note is empty",
        description:
          generationError ||
          "The pipeline finished but produced no note. Check the lambda logs.",
      });
      return;
    }

    setCompare({
      // The final note on the patient record — what Review Note shows in its
      // "Final note" panel, edits included — not the raw note the summarization
      // job produced.
      originalNote: finalNote || "",
      testNote,
      generationError,
      convPromptName: run.conv_prompt_name,
      convPromptVersion: run.conv_prompt_version,
      // Lets the modal tell "promote this version" apart from "it already is
      // the default", rather than firing a no-op set-default call.
      currentDefaultVersion:
        versions.find((v) => v.is_default === true)?.version || null,
      testJobName: run.test_summarize_job_name,
    });
  }, [
    patientId,
    running,
    convPromptName,
    selectedVersion,
    versions,
    finalNote,
    pollForResult,
    toast,
  ]);

  // Test-note runs have their own cross-environment capability, independent of
  // the commercial-only dataset evaluation tools that share the API module.
  if (!isCapabilityEnabled("testNote")) return null;

  const renderPickerBody = () => {
    if (loadingVersions) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-ktx-purple" />
          <p className="ml-2 text-gray-600">Loading prompt versions…</p>
        </div>
      );
    }

    if (versionsError) {
      return <p className="text-red-600 py-6">{versionsError}</p>;
    }

    return (
      <>
        <div className="text-sm text-gray-600">
          Re-runs{" "}
          <span className="font-mono text-gray-800">
            {sourceJob?.summarize_job_name}
          </span>{" "}
          using{" "}
          <span className="font-medium text-gray-800">{convPromptName}</span> at
          the version you pick. The result is stored as a TEST row and never
          replaces the production note.
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
          {versions.map((version) => {
            const checked = selectedVersion === version.version;
            const isDefault = version.is_default === true;
            const displayTime = getTemplateDisplayTime(version);
            return (
              <label
                key={version.version}
                className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer ${
                  checked
                    ? "border-ktx-purple bg-purple-50"
                    : isDefault
                    ? "border-purple-200 bg-purple-50/40"
                    : "border-gray-200"
                }`}
              >
                {/* ktx-purple is registered per-utility (background/text/border)
                    rather than as a theme color, so `accent-ktx-purple` would
                    not generate — hence the literal brand value. */}
                <input
                  type="radio"
                  name="test-note-prompt-version"
                  checked={checked}
                  onChange={() => setSelectedVersion(version.version)}
                  className="h-4 w-4 accent-[#783BFF]"
                />
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2">
                    {isDefault && (
                      <Badge
                        variant="outline"
                        className="text-xs font-semibold border-purple-200 bg-purple-50 text-ktx-purple"
                      >
                        Default
                      </Badge>
                    )}
                    <span className="font-medium text-gray-900">
                      {version.version || "v1.0"}
                    </span>
                  </div>
                  {(version.username || displayTime) && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {version.username && (
                        <span className="font-medium text-gray-600">
                          {version.username}
                        </span>
                      )}
                      {version.username && displayTime && <span> · </span>}
                      {displayTime && formatTimestamp(displayTime)}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setPickerOpen(true)}
        disabled={running || !patientId}
        className="border-ktx-purple text-ktx-purple hover:bg-purple-50"
      >
        {running ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating…
          </>
        ) : (
          <>
            <FlaskConical className="h-4 w-4" />
            Test Note
          </>
        )}
      </Button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="w-[95vw] max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
              <FlaskConical className="h-6 w-6 shrink-0" />
              Test Note
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-1">
              Pick the CONVERSATION_PROMPT version to generate this note with.
            </DialogDescription>
          </DialogHeader>

          {renderPickerBody()}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPickerOpen(false)}
              className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRun}
              disabled={!selectedVersion || loadingVersions}
              className="bg-ktx-purple hover:bg-purple-700 text-white disabled:opacity-50"
            >
              Generate Test Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {compare && (
        <TestNoteCompareModal
          open
          onOpenChange={(next) => {
            if (!next) setCompare(null);
          }}
          patientId={patientId}
          {...compare}
          onDefaultSwitched={(version) => {
            // Keep the cached list in step with the change so reopening the
            // picker marks the right version as default.
            setVersions((prev) =>
              prev.map((v) => ({ ...v, is_default: v.version === version }))
            );
            setCompare((prev) =>
              prev ? { ...prev, currentDefaultVersion: version } : prev
            );
          }}
        />
      )}
    </>
  );
};

export default TestNoteAction;
